// ============================================================================
// SESSION TASK DETAIL MODAL
// Shows step-by-step progress and outputs for an n8n session task
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  X,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Loader,
  RotateCcw,
  FileInput,
  ZoomIn,
  Lightbulb,
  Zap,
  Palette,
  Link,
  Download,
  Monitor,
  ExternalLink,
  Eye,
  EyeOff,
  Printer,
  ImageIcon
} from 'lucide-react';
import { api } from '../../services/api';
import sessionTasksService from '../../services/sessionTasksService';
import { PublishToHubButton } from './PublishToHubButton';
import { CreateSessionTaskInput } from '../../types/session-task.types';
import { MermaidDiagram } from '../diagrams/MermaidDiagram';
import { GenerateImageModal } from '../GenerateImageModal';
import { generateSessionImage, GenerateImageParams } from '../../services/imageGen';
import {
  DELIVERABLE_OUTPUT_TYPES,
  MAX_ZIP_BINARY_BYTES,
  deliverableFileName,
  deliverableKindLabel,
  downloadUrlAsFile,
  formatBytes,
  parseDeliverableContent,
} from '../../utils/deliverables';

/** Opens the Open Design web UI deep-linked to a specific project */
const OpenDesignEditorButton: React.FC<{ sessionId: string; workflowId: string }> = ({ sessionId, workflowId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; url: string }>(
        `/sessions/${sessionId}/tasks/${workflowId}/design-url`
      );
      if (res.data.success && res.data.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      } else {
        setError('Could not retrieve design editor URL');
      }
    } catch {
      setError('Open Design is not configured');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 flex items-center gap-3">
      <button
        onClick={handleOpen}
        disabled={loading}
        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-60"
      >
        {loading ? <Loader size={14} className="animate-spin" /> : <Palette size={14} />}
        Open in Design Editor
      </button>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
};

/** Pushes a completed html_artifact to an Open Design project so it can be edited */
const PushToOpenDesignButton: React.FC<{ sessionId: string; workflowId: string }> = ({ sessionId, workflowId }) => {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [odUrl, setOdUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePush = async () => {
    setState('loading');
    setError(null);
    try {
      const res = await api.post<{ success: boolean; url: string; error?: string }>(
        `/sessions/${sessionId}/tasks/${workflowId}/push-to-open-design`
      );
      if (res.data.success && res.data.url) {
        setOdUrl(res.data.url);
        setState('done');
      } else {
        setError(res.data.error || 'Push failed');
        setState('error');
      }
    } catch {
      setError('Could not push to Open Design');
      setState('error');
    }
  };

  if (state === 'done' && odUrl) {
    return (
      <div className="mt-4 flex items-center gap-3">
        <a
          href={odUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <Palette size={14} />
          Open in Design Editor
        </a>
        <span className="text-xs text-slate-400">Pushed — open link above to edit</span>
      </div>
    );
  }

  return (
    <div className="mt-4 flex items-center gap-3">
      <button
        onClick={handlePush}
        disabled={state === 'loading'}
        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-60"
      >
        {state === 'loading' ? <Loader size={14} className="animate-spin" /> : <Palette size={14} />}
        {state === 'loading' ? 'Pushing…' : 'Push to Open Design'}
      </button>
      {state === 'error' && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
};

/** Image lightbox overlay for viewing images fullscreen */
export const ImageLightbox: React.FC<{ src: string; alt: string; onClose: () => void }> = ({ src, alt, onClose }) => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => { // NOSONAR
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => { // NOSONAR
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/85 z-[2000] flex items-center justify-center cursor-[zoom-out] p-10"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 bg-white/15 border-none rounded-full w-10 h-10 flex items-center justify-center cursor-pointer text-white hover:bg-white/25 transition-colors"
      >
        <X size={22} />
      </button>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="w-[95vw] h-[90vh] object-contain rounded-lg shadow-[0_8px_40px_rgba(0,0,0,0.5)] cursor-default"
      />
    </div>
  );
};

interface SessionTaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  workflowId: string;
  executionId: string;
  onRerun?: (workflowId: string) => void;
  onAddToContext?: (workflowId: string) => void;
  onPushToScreen?: (item: { type: 'markdown' | 'image' | 'mermaid' | 'html'; content: string; title?: string; source: string }) => void;
  onIterateSuccess?: (workflowId: string, executionId: string) => void;
  /** The session's project, for sharing file outputs on a Session Hub */
  projectId?: string;
}

interface StepResult {
  stepId: string;
  stepName: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  output?: any;
  error?: string;
}

interface TaskOutput {
  type: string;
  content: any;
  source: string;
}

interface TaskDetail {
  executionId: string;
  workflowId: string;
  taskName: string;
  taskCategory: string;
  templateId: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  currentStepName?: string;
  stepResults: StepResult[];
  outputs: TaskOutput[];
}

const isMarkdown = (text: string): boolean => {
  if (!text || typeof text !== 'string') return false;
  const patterns = [
    /^#{1,6}\s/m,
    /\*\*[^*]+\*\*/,
    /\[[^\]]+\]\([^)]+\)/,
    /^[-*+]\s/m,
    /^\d+\.\s/m,
    /```[\s\S]*```/,
    /^>\s/m,
    /^\|.+\|$/m,
  ];
  return patterns.some(p => p.test(text));
};

const MARKDOWN_COMPONENTS = {
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h1: ({ node, ...props }: any) => <h1 className="text-xl font-bold mt-0 mb-3 border-b border-slate-200 pb-2" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h2: ({ node, ...props }: any) => <h2 className="text-lg font-semibold mt-4 mb-2" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content
  h3: ({ node, ...props }: any) => <h3 className="text-base font-semibold mt-3 mb-1.5" {...props} />,
  p: ({ node, ...props }: any) => <p className="mb-2.5 leading-relaxed" {...props} />,
  ul: ({ node, ...props }: any) => <ul className="ml-5 mb-2.5" {...props} />,
  ol: ({ node, ...props }: any) => <ol className="ml-5 mb-2.5" {...props} />,
  li: ({ node, ...props }: any) => <li className="mb-1" {...props} />,
  code: ({ node, inline, ...props }: any) =>
    inline
      ? <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[13px] font-mono" {...props} />
      : <code className="block bg-slate-50 p-3 rounded text-[13px] font-mono overflow-auto" {...props} />,
  pre: ({ node, ...props }: any) => <pre className="mb-3" {...props} />,
  blockquote: ({ node, ...props }: any) => <blockquote className="border-l-4 border-slate-300 pl-4 ml-0 text-slate-500 mb-3" {...props} />,
  strong: ({ node, ...props }: any) => <strong className="font-semibold" {...props} />,
  table: ({ node, ...props }: any) => <table className="border-collapse w-full mb-3 text-[13px]" {...props} />,
  thead: ({ node, ...props }: any) => <thead className="bg-slate-50" {...props} />,
  th: ({ node, ...props }: any) => <th className="border border-slate-200 p-2 text-left font-semibold" {...props} />,
  td: ({ node, ...props }: any) => <td className="border border-slate-200 p-2" {...props} />,
};

const MarkdownContent: React.FC<{ content: string }> = ({ content }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm leading-relaxed overflow-auto max-h-[500px]">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
      {content}
    </ReactMarkdown>
  </div>
);

/** Detect if a string looks like Mermaid diagram code */
const isMermaidCode = (text: string): boolean => {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  return /^(graph\s|flowchart\s|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|gitGraph|journey|C4Context|C4Container|C4Component|C4Deployment)/m.test(trimmed);
};

/** Try to parse a JSON string and extract a krokiUrl */
const tryParseImageOutput = (content: any): { krokiUrl?: string; diagramType?: string; perspective?: string } | null => {
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      if (parsed && parsed.krokiUrl) return parsed;
    } catch { /* not JSON */ }
  }
  if (typeof content === 'object' && content?.krokiUrl) return content;
  return null;
};

/** Scaffold Bundle download renderer */
const ScaffoldBundleRenderer: React.FC<{ content: any }> = ({ content }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  let data: { s3Key?: string; fileName?: string; fileSize?: number };
  try {
    data = typeof content === 'string' ? JSON.parse(content) : content;
  } catch {
    return <pre className="text-xs">{JSON.stringify(content, null, 2)}</pre>;
  }

  const handleDownload = async () => {
    if (!data.s3Key) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      // Extract projectId from s3Key: projects/{projectId}/builds/...
      const parts = data.s3Key.split('/');
      const projectId = parts[1]; // projects/{projectId}/...
      const response = await api.get(
        `/api/projects/${projectId}/s3/download?key=${encodeURIComponent(data.s3Key)}`
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (err: any) {
      setDownloadError('Failed to generate download URL: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  let fileSizeLabel: string | null = null;
  if (data.fileSize) {
    fileSizeLabel = data.fileSize > 1024 * 1024
      ? `${(data.fileSize / (1024 * 1024)).toFixed(1)} MB`
      : `${(data.fileSize / 1024).toFixed(1)} KB`;
  }

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
      <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
        <Download size={24} className="text-emerald-600" />
      </div>
      <h4 className="text-lg font-bold text-emerald-900 mb-1">Scaffold Bundle Ready</h4>
      <p className="text-sm text-emerald-700 mb-1">
        {data.fileName || 'scaffold.zip'}
        {fileSizeLabel && <span className="text-emerald-500 ml-1">({fileSizeLabel})</span>}
      </p>
      <p className="text-xs text-emerald-600 mb-4">
        Contains CLAUDE.md, spec files, and design references. Unzip and run <code className="bg-emerald-100 px-1 rounded">claude</code> to start building.
      </p>
      <button
        onClick={handleDownload}
        disabled={downloading || !data.s3Key}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {downloading ? (
          <>
            <Loader size={16} className="animate-spin" />
            Generating URL...
          </>
        ) : (
          <>
            <Download size={16} />
            Download Bundle
          </>
        )}
      </button>
      {downloadError && (
        <p className="text-sm text-red-600 mt-3">{downloadError}</p>
      )}
    </div>
  );
};

/** S3-backed markdown renderer — fetches content via presigned URL, renders as markdown */
const S3MarkdownRenderer: React.FC<{ content: any }> = ({ content }) => {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const data = useMemo<{ s3Key?: string; format?: string; title?: string; description?: string } | null>(() => {
    try {
      return typeof content === 'string' ? JSON.parse(content) : content;
    } catch {
      return null;
    }
  }, [content]);

  const projectId = data?.s3Key?.split('/')[1];

  const fetchContent = useCallback(async () => {
    if (!data?.s3Key || !projectId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/api/projects/${projectId}/s3/download?key=${encodeURIComponent(data.s3Key)}`
      );
      if (response.data.downloadUrl) {
        const mdResponse = await fetch(response.data.downloadUrl);
        const text = await mdResponse.text();
        setMarkdown(text);
      }
    } catch (err: any) {
      setError('Failed to load content: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [data?.s3Key, projectId]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  if (!data) return <pre className="text-xs">{JSON.stringify(content, null, 2)}</pre>;

  const handleDownload = async () => {
    if (!data.s3Key || !projectId) return;
    setDownloading(true);
    try {
      const response = await api.get(
        `/api/projects/${projectId}/s3/download?key=${encodeURIComponent(data.s3Key)}`
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch {
      // silent
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveToKB = async () => {
    if (!data.s3Key || !projectId) return;
    setSaving(true);
    try {
      await api.post(`/api/projects/${projectId}/outputs`, {
        name: data.title || 'Competitive Analysis Report',
        type: 'workflow',
        format: data.format || 'markdown',
        existingS3Key: data.s3Key,
        creatorSource: 'workflow',
        workflowContext: {
          workflowName: 'Session-Task-Competitive-Analysis',
          stepName: 'Final Report'
        }
      });
      setSaved(true);
    } catch (err: any) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400">
        <Loader size={16} className="animate-spin mr-2" /> Loading report...
      </div>
    );
  }

  if (error && !markdown) {
    return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>;
  }

  return (
    <div>
      {/* Action buttons */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <Download size={14} />
          {downloading ? 'Downloading...' : 'Download .md'}
        </button>
        {!saved ? (
          <button
            onClick={handleSaveToKB}
            disabled={saving || !markdown}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            <Lightbulb size={14} />
            {saving ? 'Adding...' : 'Add to Knowledge'}
          </button>
        ) : (
          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600">
            <CheckCircle size={14} /> Added — ingesting to vector store &amp; graph
          </span>
        )}
      </div>
      {error && <div className="text-xs text-red-500 mb-2">{error}</div>}
      {/* Markdown content */}
      {markdown && <MarkdownContent content={markdown} />}
    </div>
  );
};

/** Resolve S3 key to a presigned URL via the backend */
async function resolveS3PresignedUrl(bucket: string, key: string): Promise<string> {
  const response = await api.post('/api/s3/presigned-url', { bucket, key, expiresIn: 3600 });
  return response.data.url;
}

/** BCAF Assessment visual scorecard renderer */
const BcafAssessmentRenderer: React.FC<{ content: any }> = ({ content }) => {
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  let data: any;
  try {
    data = typeof content === 'string' ? JSON.parse(content) : content;
  } catch {
    return (
      <pre className="bg-white border border-slate-200 rounded-lg p-3 text-[13px] font-mono overflow-auto max-h-[400px] whitespace-pre-wrap break-words m-0">
        {String(content)}
      </pre>
    );
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'passed': return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' };
      case 'partial': return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' };
      case 'failed': return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-100 text-red-700' };
      default: return { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600' };
    }
  };

  const overallColors = statusColor(data.overallStatus);

  const categoryLabels: Record<string, string> = {
    operational: 'Operational',
    commercial: 'Commercial',
    technology: 'Technology'
  };

  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Overall Status */}
      <div className={`flex items-center justify-between ${overallColors.bg} border ${overallColors.border} rounded-lg px-4 py-3`}>
        <span className="text-sm font-semibold text-slate-700">Overall Assessment</span>
        <span className={`px-3 py-1 rounded-full text-sm font-bold ${overallColors.badge}`}>
          {data.overallStatus?.toUpperCase()}
        </span>
      </div>

      {/* Category Scores */}
      {data.categoryScores && (
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(data.categoryScores).map(([category, scores]: [string, any]) => {
            const total = scores.total || 0;
            const passedPct = total > 0 ? (scores.passed / total) * 100 : 0;
            const partialPct = total > 0 ? (scores.partial / total) * 100 : 0;
            return (
              <div key={category} className="border border-slate-200 rounded-lg p-3 bg-white">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  {categoryLabels[category] || category}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-bold text-slate-800">{scores.passed}/{total}</span>
                  <span className="text-xs text-slate-500">passed</span>
                </div>
                {/* Score bar */}
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden flex">
                  <div className="bg-emerald-400 h-full" style={{ width: `${passedPct}%` }} />
                  <div className="bg-amber-400 h-full" style={{ width: `${partialPct}%` }} />
                </div>
                <div className="flex gap-3 mt-1.5 text-[11px] text-slate-500">
                  {scores.passed > 0 && <span className="text-emerald-600">{scores.passed} passed</span>}
                  {scores.partial > 0 && <span className="text-amber-600">{scores.partial} partial</span>}
                  {scores.failed > 0 && <span className="text-red-600">{scores.failed} failed</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Per-Question Results */}
      {data.results && data.results.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Question Details</span>
          </div>
          <div className="divide-y divide-slate-100">
            {data.results.map((result: any) => {
              const colors = statusColor(result.status);
              const isExpanded = expandedQuestions.has(result.questionId);
              return (
                <div key={result.questionId} className="bg-white">
                  <button
                    onClick={() => toggleQuestion(result.questionId)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                  >
                    {isExpanded ? <ChevronDown size={14} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                    <span className={`flex-shrink-0 px-2 py-0.5 rounded text-[11px] font-bold ${colors.badge}`}>
                      {result.status.toUpperCase()}
                    </span>
                    <span className="text-[11px] text-slate-400 font-mono flex-shrink-0">{result.questionId}</span>
                    <span className="text-sm text-slate-700 font-medium truncate">{
                      // Get title from question ID mapping
                      (() => {
                        const titles: Record<string, string> = {
                          op_1: 'Alignment to strategic objective', op_2: 'Senior-level sponsorship',
                          op_3: 'Identify all stakeholders', op_4: 'Map end-to-end process',
                          op_5: 'Drivers, insights, and risks', op_6: 'Capture feedback, address concerns',
                          op_7: 'Early adopters and advocates', com_1: 'Success criteria',
                          com_2: 'Quantify projected benefits', tech_1: 'Align talent and technology',
                          tech_2: 'Is AI best?', tech_3: 'Identify and mitigate risks'
                        };
                        return titles[result.questionId] || result.questionId;
                      })()
                    }</span>
                    {result.confidence !== undefined && (
                      <span className="ml-auto flex-shrink-0 text-[11px] text-slate-400">{Math.round(result.confidence * 100)}%</span>
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 pl-12">
                      <div className="text-sm text-slate-700 leading-relaxed mb-2">
                        <MarkdownContent content={result.answer} />
                      </div>
                      {result.evidence && result.evidence.length > 0 && (
                        <div className="mt-2">
                          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Evidence</div>
                          <div className="space-y-1">
                            {result.evidence.map((e: string) => (
                              <div key={e} className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2 italic">
                                "{e}"
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/** Render content based on output type hint */
const MermaidWithRaw: React.FC<{ code: string }> = ({ code }) => (
  <div>
    <MermaidDiagram code={code} theme="default" />
    <details className="mt-3">
      <summary className="cursor-pointer text-xs text-slate-500 font-medium">View raw code</summary>
      <pre className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono overflow-auto max-h-[300px]">
        {code}
      </pre>
    </details>
  </div>
);

const renderDiagramImage = (
  content: any,
  onImageClick?: (img: { src: string; alt: string }) => void,
): React.ReactNode => {
  const imageData = tryParseImageOutput(content);
  if (!imageData?.krokiUrl) return null;
  return (
    <div>
      <div
        onClick={() => onImageClick?.({ src: imageData.krokiUrl!, alt: 'Architecture Diagram' })}
        className="bg-white border border-slate-200 rounded-lg p-4 text-center relative"
        style={{ cursor: onImageClick ? 'zoom-in' : 'default' }}
      >
        <img
          src={imageData.krokiUrl}
          alt="Architecture Diagram"
          className="max-w-full h-auto"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            (e.target as HTMLImageElement).nextElementSibling?.removeAttribute('style');
          }}
        />
        <div style={{ display: 'none' }} className="text-slate-500 text-[13px] p-5">
          Failed to load diagram image.{' '}
          <a href={imageData.krokiUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500">
            Open directly
          </a>
        </div>
        {onImageClick && (
          <div className="absolute bottom-6 left-6 bg-black/50 text-white py-0.5 px-2 rounded text-[11px] flex items-center gap-1 opacity-70">
            <ZoomIn size={12} />
            Click to enlarge
          </div>
        )}
      </div>
      {imageData.diagramType && (
        <div className="mt-2 text-xs text-slate-500">
          Type: {imageData.diagramType}
          {imageData.perspective && ` | Perspective: ${imageData.perspective}`}
        </div>
      )}
    </div>
  );
};

const VIEWER_TYPE_LABELS: Record<string, string> = {
  html_artifact: 'HTML Artifact',
  pdf_document: 'PDF Document',
  video: 'Video',
  image: 'Image',
};

/**
 * Inline viewer for S3-hosted file outputs (html_artifact, pdf_document, video, image).
 * presignedUrl expires after an hour. Iframes keep their first URL so a refresh doesn't
 * reload the page the user is reading; media elements and buttons use the latest URL.
 * A load error triggers one refetch via onRefreshUrl; the allowance resets after a successful load.
 */
export interface UrlRefresh {
  /** Refetch the task execution so outputs carry a fresh presignedUrl. */
  refresh: () => Promise<void> | void;
  /** When the current outputs were fetched (ms epoch). */
  issuedAt: number;
}

const URL_STALE_MS = 50 * 60 * 1000;

const FileOutputViewer: React.FC<{
  content: any;
  outputType: string;
  urlRefresh?: UrlRefresh;
}> = ({ content, outputType, urlRefresh }) => {
  const onRefreshUrl = urlRefresh?.refresh;
  const data = parseDeliverableContent(content);
  const url = data.presignedUrl;
  const [frameSrc, setFrameSrc] = useState<string | undefined>(url);
  const latestUrl = React.useRef(url);
  latestUrl.current = url;
  const [refreshing, setRefreshing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const errorRefreshUsed = React.useRef(false);
  const reloadFrameOnNextUrl = React.useRef(false);
  const resumeAt = React.useRef<number | null>(null);
  const issuedAt = urlRefresh?.issuedAt ?? Date.now();
  const mountRefreshDone = React.useRef(false);

  useEffect(() => {
    if (!frameSrc || reloadFrameOnNextUrl.current) {
      reloadFrameOnNextUrl.current = false;
      setFrameSrc(url);
    }
    // frameSrc intentionally excluded: it only follows url on first load or after an error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const refresh = useCallback(async () => {
    if (!onRefreshUrl) return;
    setRefreshing(true);
    try { await onRefreshUrl(); } finally { setRefreshing(false); }
  }, [onRefreshUrl]);

  // On mount, refetch once if the URL is close to expiry (e.g. output re-expanded after a long wait).
  useEffect(() => {
    if (mountRefreshDone.current) return;
    mountRefreshDone.current = true;
    if (onRefreshUrl && Date.now() - issuedAt > URL_STALE_MS) {
      reloadFrameOnNextUrl.current = true;
      void refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadError = (e?: React.SyntheticEvent<HTMLVideoElement>) => {
    if (errorRefreshUsed.current || !onRefreshUrl) {
      setLoadFailed(true);
      return;
    }
    errorRefreshUsed.current = true;
    if (e?.currentTarget && e.currentTarget.currentTime > 0) resumeAt.current = e.currentTarget.currentTime;
    reloadFrameOnNextUrl.current = true;
    void refresh();
  };

  const handleLoaded = () => {
    errorRefreshUsed.current = false;
    setLoadFailed(false);
  };

  const ensureFreshUrl = async (): Promise<string | undefined> => {
    if (onRefreshUrl && Date.now() - issuedAt > URL_STALE_MS) {
      await refresh();
      await new Promise(resolve => setTimeout(resolve, 0)); // let the refreshed task render
    }
    return latestUrl.current;
  };

  const fileName = deliverableFileName(outputType, data, 'output');
  const tooBigForBlob = (data.sizeBytes ?? 0) > MAX_ZIP_BINARY_BYTES;

  const handleDownload = async () => {
    const current = await ensureFreshUrl();
    if (!current) return;
    if (tooBigForBlob) {
      window.open(current, '_blank', 'noopener,noreferrer');
      return;
    }
    setDownloading(true);
    try {
      await downloadUrlAsFile(current, fileName);
    } catch {
      window.open(current, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  };

  const handleOpen = async () => {
    const current = await ensureFreshUrl();
    if (current) window.open(current, '_blank', 'noopener,noreferrer');
  };

  const meta = [
    data.kind ? deliverableKindLabel(data.kind) : null,
    VIEWER_TYPE_LABELS[outputType],
    formatBytes(data.sizeBytes) || null,
  ].filter(Boolean).join(' · ');

  let viewer: React.ReactNode = null;
  if (url) {
    if (outputType === 'html_artifact') {
      viewer = (
        <iframe
          key={frameSrc}
          src={frameSrc}
          title={data.title || fileName}
          sandbox="allow-scripts"
          onLoad={handleLoaded}
          onError={() => handleLoadError()}
          className="w-full h-[75vh] rounded-lg border border-slate-200 bg-white"
        />
      );
    } else if (outputType === 'pdf_document') {
      viewer = (
        <iframe
          key={frameSrc}
          src={frameSrc}
          title={data.title || fileName}
          onLoad={handleLoaded}
          onError={() => handleLoadError()}
          className="w-full h-[75vh] rounded-lg border border-slate-200 bg-white"
        />
      );
    } else if (outputType === 'video') {
      viewer = (
        <video
          controls
          preload="metadata"
          src={url}
          onLoadedMetadata={(e) => {
            handleLoaded();
            if (resumeAt.current !== null) {
              e.currentTarget.currentTime = resumeAt.current;
              resumeAt.current = null;
            }
          }}
          onError={handleLoadError}
          className="w-full max-h-[75vh] rounded-lg border border-slate-200 bg-black"
        />
      );
    } else if (outputType === 'image') {
      viewer = (
        <img
          src={url}
          alt={data.title || fileName}
          onLoad={handleLoaded}
          onError={() => handleLoadError()}
          className="max-w-full h-auto mx-auto rounded-lg border border-slate-200"
        />
      );
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 mb-0.5">{data.title || data.fileName || VIEWER_TYPE_LABELS[outputType] || 'File'}</p>
          {data.description && <p className="text-sm text-slate-600 mb-1">{data.description}</p>}
          {data.fileName && data.title !== data.fileName && (
            <p className="text-xs text-slate-500 font-mono truncate">{data.fileName}</p>
          )}
          {meta && <p className="text-xs text-slate-400 mt-1">{meta}</p>}
        </div>
        {url && (
          <div className="flex items-center gap-2 shrink-0">
            {(outputType === 'html_artifact' || outputType === 'pdf_document') && (
              <button
                type="button"
                onClick={handleOpen}
                className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors"
              >
                <ExternalLink size={14} />
                Open in new tab
              </button>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              title={tooBigForBlob ? 'Large file: opens in a new tab, save from there' : undefined}
              className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 bg-white text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              {downloading ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
              Download
            </button>
          </div>
        )}
      </div>
      {refreshing && <p className="text-xs text-slate-400">Refreshing link…</p>}
      {loadFailed && (
        <p className="text-sm text-amber-600">
          The file didn't load. Close and reopen this task to get a fresh link.
        </p>
      )}
      {!url && (
        <p className="text-sm text-amber-600">URL unavailable — reopen this task to refresh the link</p>
      )}
      {viewer}
    </div>
  );
};

const parseS3MarkdownCandidate = (content: any): any | null => {
  if (!content) return null;
  if (typeof content === 'object') return content;
  if (typeof content === 'string') {
    try {
      const p = JSON.parse(content);
      return p && typeof p === 'object' ? p : null;
    } catch {
      return null;
    }
  }
  return null;
};

const MARKDOWN_OUTPUT_TYPES = new Set(['synthesis', 'conversation', 'research_findings', 'competitive_matrix']);
const S3_REPORT_TYPES = new Set(['competitive_analysis', 'customer_research_report']);

export const renderOutputContent = (
  content: any,
  outputType?: string,
  onImageClick?: (img: { src: string; alt: string }) => void,
  urlRefresh?: UrlRefresh,
): React.ReactNode => {
  if (outputType === 'diagram_code' && typeof content === 'string' && content.trim()) {
    return <MermaidWithRaw code={content} />;
  }
  if (outputType === 'avatar_portrait' && content?.imageUrl) {
    return (
      <div className="text-center">
        <img
          src={content.imageUrl}
          alt="Avatar Portrait"
          className="max-w-sm mx-auto rounded-xl border border-slate-200 shadow-sm"
          onClick={() => onImageClick?.({ src: content.imageUrl, alt: 'Avatar Portrait' })}
          style={{ cursor: onImageClick ? 'zoom-in' : 'default' }}
        />
      </div>
    );
  }
  if (outputType === 'diagram_image') return renderDiagramImage(content, onImageClick);
  if (outputType && DELIVERABLE_OUTPUT_TYPES.has(outputType)) {
    return <FileOutputViewer content={content} outputType={outputType} urlRefresh={urlRefresh} />;
  }
  if (outputType === 'bcaf_assessment') return <BcafAssessmentRenderer content={content} />;
  if (outputType === 'scaffold_bundle') return <ScaffoldBundleRenderer content={content} />;
  if (outputType && S3_REPORT_TYPES.has(outputType)) return <S3MarkdownRenderer content={content} />;

  const s3Candidate = parseS3MarkdownCandidate(content);
  if (s3Candidate?.format === 'markdown' && s3Candidate?.s3Key) {
    return <S3MarkdownRenderer content={s3Candidate} />;
  }
  if (outputType && MARKDOWN_OUTPUT_TYPES.has(outputType) && typeof content === 'string') {
    return <MarkdownContent content={content} />;
  }
  if (typeof content === 'string' && isMermaidCode(content)) return <MermaidWithRaw code={content} />;
  if (typeof content === 'string') {
    if (isMarkdown(content)) return <MarkdownContent content={content} />;
    return (
      <pre className="bg-white border border-slate-200 rounded-lg p-3 text-[13px] font-mono overflow-auto max-h-[400px] whitespace-pre-wrap break-words m-0">
        {content}
      </pre>
    );
  }
  return (
    <pre className="bg-white border border-slate-200 rounded-lg p-3 text-xs font-mono overflow-auto max-h-[400px] m-0">
      {JSON.stringify(content, null, 2)}
    </pre>
  );
};

/** Render content without type hint (for step outputs) */
const renderContent = (content: any): React.ReactNode => renderOutputContent(content);

/** Determine what push-to-screen content type an output maps to */
const getPushContentType = (content: any, outputType?: string): 'markdown' | 'image' | 'mermaid' | null => {
  if (outputType === 'diagram_code') return 'mermaid';
  if (outputType === 'diagram_image') {
    const parsed = tryParseImageOutput(content);
    return parsed?.krokiUrl ? 'image' : null;
  }
  // Not pushable types
  if (outputType === 'scaffold_bundle') return null;
  // S3-backed reports — not directly pushable (content is a reference, not the text)
  if (outputType === 'competitive_analysis' || outputType === 'customer_research_report') return null;
  if (typeof content === 'string') {
    if (isMermaidCode(content)) return 'mermaid';
    return 'markdown';
  }
  // Objects with s3Key are references, not pushable directly
  if (typeof content === 'object' && content?.s3Key) return null;
  return 'markdown'; // JSON objects get stringified
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'thinking': return <Lightbulb size={14} />;
    case 'making': return <Palette size={14} />;
    default: return <Zap size={14} />;
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'running':
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold">
          <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
          Running
        </div>
      );
    case 'completed':
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-sm font-semibold">
          <CheckCircle size={14} />
          Completed
        </div>
      );
    case 'failed':
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-600 text-sm font-semibold">
          <XCircle size={14} />
          Failed
        </div>
      );
    case 'cancelled':
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-600 text-sm font-semibold">
          <AlertCircle size={14} />
          Cancelled
        </div>
      );
    default:
      return (
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-semibold">
          <Clock size={14} />
          {status}
        </div>
      );
  }
};

const getStepStatusIcon = (status: string) => {
  switch (status) {
    case 'running':
      return <Loader size={16} className="text-blue-500 animate-spin" />;
    case 'completed':
      return <CheckCircle size={16} className="text-emerald-500" />;
    case 'failed':
      return <XCircle size={16} className="text-red-500" />;
    case 'cancelled':
      return <AlertCircle size={16} className="text-amber-500" />;
    default:
      return <Clock size={16} className="text-slate-400" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'running': return 'text-blue-500';
    case 'completed': return 'text-emerald-500';
    case 'failed': return 'text-red-500';
    case 'cancelled': return 'text-amber-500';
    default: return 'text-slate-400';
  }
};

const getStatusBgColor = (status: string) => {
  switch (status) {
    case 'running': return 'bg-blue-50 text-blue-600';
    case 'completed': return 'bg-emerald-50 text-emerald-600';
    case 'failed': return 'bg-red-50 text-red-600';
    case 'cancelled': return 'bg-amber-50 text-amber-600';
    default: return 'bg-slate-100 text-slate-500';
  }
};

const formatDuration = (startedAt: string, completedAt?: string) => {
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - start;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const OUTPUT_TYPE_LABELS: Record<string, string> = {
  conversation: 'Discussion',
  synthesis: 'Synthesis Report',
  diagram_code: 'Diagram Code',
  diagram_image: 'Diagram Image',
  research_findings: 'Research Findings',
  competitive_matrix: 'Competitive Matrix',
  scaffold_bundle: 'Scaffold Bundle',
  competitive_analysis: 'Competitive Analysis',
  bcaf_assessment: 'BCAF Assessment',
  html_artifact: 'HTML Artifact',
  pdf_document: 'PDF Document',
  video: 'Video',
  customer_research_report: 'Customer Research Report',
  customer_research_entities: 'Discovered Entities',
  graph_sync_result: 'Graph Sync Result',
  avatar_portrait: 'Avatar Portrait',
};

export const getOutputTypeLabel = (type: string): string =>
  OUTPUT_TYPE_LABELS[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const OUTPUT_TYPE_BADGES: Record<string, string> = {
  synthesis: 'Summary',
  diagram_code: 'Mermaid',
  diagram_image: 'Image',
  research_findings: 'Markdown',
  competitive_matrix: 'Table',
  scaffold_bundle: 'Download',
  competitive_analysis: 'Report',
  bcaf_assessment: 'Assessment',
  conversation: 'Text',
  customer_research_report: 'Report',
  customer_research_entities: 'Graph Data',
  graph_sync_result: 'Sync',
  avatar_portrait: 'Image',
  html_artifact: 'HTML',
  pdf_document: 'PDF',
  video: 'MP4',
  image: 'Image',
};

export const getOutputTypeBadge = (type: string): string =>
  OUTPUT_TYPE_BADGES[type] ?? type.split('_')[0];

export const SessionTaskDetailModal: React.FC<SessionTaskDetailModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  workflowId,
  executionId,
  onRerun,
  onAddToContext,
  onPushToScreen,
  onIterateSuccess,
  projectId
}) => {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [pushedOutputs, setPushedOutputs] = useState<Set<string>>(new Set());
  const [published, setPublished] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [expandedOutputs, setExpandedOutputs] = useState<Set<string>>(new Set(['synthesis', 'diagram_code', 'diagram_image', 'scaffold_bundle', 'bcaf_assessment', 'customer_research_report', 'competitive_analysis', 'html_artifact', 'pdf_document', 'video', 'image']));
  const [activeTab, setActiveTab] = useState<'outputs' | 'steps'>('outputs');
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
  const [showIterateForm, setShowIterateForm] = useState(false);
  const [iteratePrompt, setIteratePrompt] = useState('');
  const [isIterating, setIsIterating] = useState(false);
  const [iterateError, setIterateError] = useState<string | null>(null);
  const [showGenerateImageModal, setShowGenerateImageModal] = useState(false);
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [imageGenCountdown, setImageGenCountdown] = useState(0);
  const isFirstLoad = React.useRef(true);
  const taskLoadedAt = React.useRef(Date.now());

  async function togglePublish(e: React.MouseEvent) {
    e.stopPropagation();
    setPublishLoading(true);
    const next = !published;
    try {
      await api.patch(`/api/sessions/${sessionId}/tasks/${workflowId}/publish`, { published: next });
      setPublished(next);
    } catch { /* silently fail */ } finally {
      setPublishLoading(false);
    }
  }

  async function handleGenerateImageFromTask(params: GenerateImageParams) {
    setImageGenLoading(true);
    setImageGenCountdown(0);
    const timer = setInterval(() => setImageGenCountdown(c => c + 1), 1000);
    try {
      await generateSessionImage(sessionId, {
        ...params,
        sourceTaskExecutionId: executionId,
      });
      setShowGenerateImageModal(false);
    } catch { /* silently fail */ } finally {
      clearInterval(timer);
      setImageGenLoading(false);
      setImageGenCountdown(0);
    }
  }

  const loadTask = useCallback(async () => {
    try {
      if (isFirstLoad.current) setLoading(true);
      const execution = await sessionTasksService.getTaskExecution(sessionId, workflowId, executionId);
      taskLoadedAt.current = Date.now();
      setTask(execution as unknown as TaskDetail);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load task details');
    } finally {
      setLoading(false);
      isFirstLoad.current = false;
    }
  }, [sessionId, workflowId, executionId]);

  const handleIterate = useCallback(async () => {
    if (!task || !iteratePrompt.trim()) return;
    const htmlOutput = task.outputs?.find(o => o.type === 'html_artifact');
    if (!htmlOutput) return;
    const outputData = typeof htmlOutput.content === 'string'
      ? JSON.parse(htmlOutput.content)
      : htmlOutput.content;
    const s3Key: string = outputData?.s3Key || '';

    setIsIterating(true);
    setIterateError(null);
    try {
      const input: CreateSessionTaskInput = {
        taskTemplateId: 'session-task-html-artifact',
        taskName: `${task.taskName} (iteration)`,
        taskCategory: task.taskCategory as 'thinking' | 'doing',
        contextSnapshot: {
          customContext: {
            text: iteratePrompt.trim(),
            iteratePrompt: iteratePrompt.trim(),
            existingHtmlS3Key: s3Key,
            parentTaskId: task.executionId,
          },
        },
      };
      const result = await sessionTasksService.createTask(sessionId, input);
      setShowIterateForm(false);
      setIteratePrompt('');
      if (onIterateSuccess) {
        onIterateSuccess(result.workflowId, result.executionId);
      } else if (onRerun) {
        onRerun(result.workflowId);
      }
    } catch (err: any) {
      setIterateError(err.message || 'Failed to create iteration');
    } finally {
      setIsIterating(false);
    }
  }, [task, iteratePrompt, sessionId, onIterateSuccess, onRerun]);

  useEffect(() => {
    if (!isOpen) {
      isFirstLoad.current = true;
      return;
    }
    loadTask();
  }, [isOpen, loadTask]);

  // Poll for updates while task is running
  useEffect(() => {
    if (task?.status !== 'running') return;
    const interval = setInterval(loadTask, 5000);
    return () => clearInterval(interval);
  }, [task?.status, loadTask]);

  const toggleStep = (stepId: string) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      next.has(stepId) ? next.delete(stepId) : next.add(stepId);
      return next;
    });
  };

  const toggleOutput = (type: string) => {
    setExpandedOutputs(prev => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  };

  const getPublishIcon = () => {
    if (publishLoading) return <Loader size={14} className="animate-spin" />;
    return published ? <Eye size={14} /> : <EyeOff size={14} />;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Image lightbox */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]"
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl w-[94%] max-w-[1200px] max-h-[92vh] overflow-hidden z-[1001] shadow-2xl flex flex-col">
        {/* Header */}
        <header className="border-b border-slate-100 p-6">
          <div className="flex flex-col md:flex-row justify-between items-start gap-4">
            <div className="space-y-3">
              <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight m-0">
                {task?.taskName || 'Loading...'}
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                {task && (
                  <>
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${
                      task.taskCategory === 'thinking' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {getCategoryIcon(task.taskCategory)}
                      {task.taskCategory.charAt(0).toUpperCase() + task.taskCategory.slice(1)}
                    </div>
                    {getStatusBadge(task.status)}
                    {task.startedAt && (
                      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">
                        <Clock size={14} />
                        {formatDuration(task.startedAt, task.completedAt)}
                      </div>
                    )}
                    {task.status === 'running' && task.currentStepName && (
                      <div className="text-slate-400 font-medium text-sm ml-1">
                        Current step: <span className="text-slate-600">{task.currentStepName}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {task && task.status === 'completed' && (
                <button
                  onClick={togglePublish}
                  disabled={publishLoading}
                  title={published ? 'Unpublish from participant view' : 'Publish to participant view'}
                  className={`flex items-center gap-2 px-4 py-2 border font-bold text-sm rounded-xl transition-colors ${
                    published
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {getPublishIcon()}
                  {published ? 'Published' : 'Publish'}
                </button>
              )}
              {task && task.status === 'completed' && onAddToContext && (
                <button
                  onClick={() => onAddToContext(task.workflowId)}
                  className="flex items-center gap-2 px-4 py-2 border border-primary text-primary font-bold text-sm rounded-xl hover:bg-primary/5 transition-colors"
                >
                  <FileInput size={14} />
                  Add to Context
                </button>
              )}
              {task && task.status === 'completed' && (
                <button
                  onClick={() => setShowGenerateImageModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <ImageIcon size={14} />
                  Generate Image
                </button>
              )}
              {task && task.status === 'completed' && task.templateId === 'session-task-html-artifact' && (
                <button
                  onClick={() => { setShowIterateForm(f => !f); setIterateError(null); }}
                  className={`flex items-center gap-2 px-4 py-2 border font-bold text-sm rounded-xl transition-colors ${
                    showIterateForm
                      ? 'border-primary/30 bg-primary/5 text-primary'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <RotateCcw size={14} />
                  Iterate
                </button>
              )}
              {task && (task.status === 'completed' || task.status === 'failed') && onRerun && (
                <button
                  onClick={() => onRerun(task.workflowId)}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <RotateCcw size={14} />
                  Rerun
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Iterate Form */}
        {showIterateForm && (
          <div className="px-6 py-4 bg-primary/5 border-b border-primary/20">
            <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Iterate on this HTML</p>
            <textarea
              value={iteratePrompt}
              onChange={e => setIteratePrompt(e.target.value)}
              placeholder="Describe what to change, e.g. 'Change the colour scheme to dark mode' or 'Add a bar chart showing the top 5 requirements by vote count'…"
              rows={3}
              className="w-full text-sm border border-slate-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
            />
            {iterateError && (
              <p className="text-xs text-red-600 mt-1">{iterateError}</p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={handleIterate}
                disabled={isIterating || !iteratePrompt.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isIterating ? <Loader size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                {isIterating ? 'Creating iteration…' : 'Run iteration'}
              </button>
              <button
                onClick={() => { setShowIterateForm(false); setIteratePrompt(''); setIterateError(null); }}
                disabled={isIterating}
                className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        {task && (
          <nav className="flex px-6 border-b border-slate-100">
            <button
              onClick={() => setActiveTab('outputs')}
              className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors ${
                activeTab === 'outputs'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              Results ({task.outputs?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('steps')}
              className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors ${
                activeTab === 'steps'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              Steps ({task.stepResults?.length || 0})
            </button>
          </nav>
        )}

        {/* Body */}
        <main className="flex-1 overflow-auto bg-slate-50 p-6 space-y-4">
          {loading && !task && (
            <div className="text-center py-10 text-slate-500">
              <Loader size={24} className="animate-spin mx-auto mb-3" />
              Loading task details...
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {task && activeTab === 'outputs' && (
            <div className="space-y-4">
              {(!task.outputs || task.outputs.length === 0) ? (
                <div className="text-center py-10 text-slate-500 bg-white rounded-xl border border-slate-200">
                  {(() => {
                    if (task.status === 'running') return 'Task is still running... Results will appear here when complete.';
                    if (task.status === 'failed') return 'Task failed before producing results.';
                    return 'No outputs available.';
                  })()}
                </div>
              ) : (
                task.outputs.map((output, outputIndex) => (
                  <section key={output.type} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div
                      onClick={() => toggleOutput(output.type)}
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                      style={expandedOutputs.has(output.type) ? { borderBottom: '1px solid #f1f5f9' } : {}}
                    >
                      <div className="flex items-center gap-3">
                        {expandedOutputs.has(output.type)
                          ? <ChevronDown size={16} className="text-slate-400" />
                          : <ChevronRight size={16} className="text-slate-400" />}
                        <h3 className="font-bold text-slate-900 m-0">
                          {getOutputTypeLabel(output.type)}
                        </h3>
                        <span className="px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          {getOutputTypeBadge(output.type)}
                        </span>
                        {output.source && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Link size={10} />
                            {output.source}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                      <PublishToHubButton
                        projectId={projectId} sessionId={sessionId} taskId={workflowId}
                        outputIndex={outputIndex} outputType={output.type}
                      />
                      {onPushToScreen && getPushContentType(output.content, output.type) && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            const pushType = getPushContentType(output.content, output.type)!;

                            {
                              let pushContent: string;
                              if (pushType === 'image' && output.type === 'diagram_image') {
                                const imageData = tryParseImageOutput(output.content);
                                pushContent = imageData?.krokiUrl || '';
                              } else {
                                pushContent = typeof output.content === 'string' ? output.content : JSON.stringify(output.content, null, 2);
                              }
                              onPushToScreen({
                                type: pushType,
                                content: pushContent,
                                title: getOutputTypeLabel(output.type),
                                source: task?.taskName || output.source || 'Task output',
                              });
                            }
                            setPushedOutputs(prev => new Set(prev).add(output.type));
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            pushedOutputs.has(output.type)
                              ? 'text-green-600 bg-green-50 border-green-200'
                              : 'text-slate-500 hover:text-primary hover:bg-primary/5 border-slate-200 hover:border-primary/30'
                          }`}
                          title="Push to second screen"
                        >
                          {pushedOutputs.has(output.type) ? (
                            <>
                              <CheckCircle size={14} />
                              Pushed
                            </>
                          ) : (
                            <>
                              <Monitor size={14} />
                              Push to Screen
                            </>
                          )}
                        </button>
                      )}
                      </div>
                    </div>

                    {expandedOutputs.has(output.type) && (
                      <div className="p-6">
                        {renderOutputContent(output.content, output.type, setLightboxImage, { refresh: loadTask, issuedAt: taskLoadedAt.current })}
                        {output.type === 'html_artifact' && output.source === 'open-design' && (
                          <OpenDesignEditorButton sessionId={sessionId} workflowId={workflowId} />
                        )}
                        {output.type === 'html_artifact' && output.source !== 'open-design' && output.source !== 'workshop-book' && output.source !== 'mcp-publish' && (
                          <PushToOpenDesignButton sessionId={sessionId} workflowId={workflowId} />
                        )}
                        {output.type === 'html_artifact' && output.source === 'workshop-book' && (() => {
                          let bookData: { presignedUrl?: string } = {};
                          try { bookData = typeof output.content === 'string' ? JSON.parse(output.content) : output.content; } catch { /* ignore */ }
                          return bookData.presignedUrl ? (
                            <button
                              onClick={() => window.open(bookData.presignedUrl, '_blank')}
                              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#0f1b4c] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                            >
                              <Printer size={14} />
                              Print Book
                            </button>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </section>
                ))
              )}
            </div>
          )}

          {task && activeTab === 'steps' && (
            <div className="space-y-2">
              {task.stepResults.length === 0 ? (
                <div className="text-center py-10 text-slate-500 bg-white rounded-xl border border-slate-200">
                  No steps recorded yet.
                </div>
              ) : (
                task.stepResults.map((step, idx) => (
                  <section key={step.stepId} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div
                      onClick={() => step.output || step.error ? toggleStep(step.stepId) : undefined}
                      className={`flex items-center justify-between p-4 ${
                        step.output || step.error ? 'cursor-pointer hover:bg-slate-50/50' : ''
                      } transition-colors`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${getStatusBgColor(step.status)}`}>
                          {idx + 1}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {step.stepName}
                          </div>
                          <div className="text-[11px] text-slate-400 font-mono">
                            {step.stepId}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium flex items-center gap-1 ${getStatusColor(step.status)}`}>
                          {getStepStatusIcon(step.status)}
                          {step.status}
                        </span>
                        {step.startedAt && step.completedAt && (
                          <span className="text-[11px] text-slate-400">
                            {formatDuration(step.startedAt, step.completedAt)}
                          </span>
                        )}
                        {(step.output || step.error) && (
                          expandedSteps.has(step.stepId)
                            ? <ChevronDown size={14} className="text-slate-400" />
                            : <ChevronRight size={14} className="text-slate-400" />
                        )}
                      </div>
                    </div>

                    {expandedSteps.has(step.stepId) && (
                      <div className="p-4 bg-slate-50 border-t border-slate-100">
                        {step.error && (
                          <div className={`bg-red-50 border border-red-200 rounded-lg p-3 text-[13px] text-red-800 ${step.output ? 'mb-3' : ''}`}>
                            {step.error}
                          </div>
                        )}
                        {step.output && renderContent(step.output)}
                      </div>
                    )}
                  </section>
                ))
              )}
            </div>
          )}
        </main>
      </div>

      {showGenerateImageModal && ReactDOM.createPortal(
        <GenerateImageModal
          onGenerate={handleGenerateImageFromTask}
          onClose={() => setShowGenerateImageModal(false)}
          loading={imageGenLoading}
          countdown={imageGenCountdown}
          taskSourceMode={true}
          taskName={task?.taskName}
        />,
        document.body
      )}
    </>
  );
};
